import React, { useState } from 'react';
import { Modal } from '@opendoor/bricks/complex';
import { Box, Flex, Heading, Text, Divider, H3, Button } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';

import { useOfferContext } from 'sellDirect/OfferContext';
import { formatInPersonEaMorningAfternoonTimeslot, useSSEAContext } from 'sellDirect/SSEAContext';
import { DatePickerWrapper } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/DatePickerWrapper';
import { LocalDate, LocalDateTime, LocalTime, ZoneId } from '@js-joda/core';
import { HeaderCornerNavButton } from 'platform/components/layout/InnerHeader';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT } from 'shared/helpers/datetime';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

const EXTERIOR_ASSESSMENT_MODAL_LABEL = 'exterior-assessment-modal';
const SCHEDULING_MODAL_TITLE_TEXT = 'Schedule your home assessment';
const SCHEDULING_MODAL_AGENT_TITLE_TEXT = 'Schedule your client’s home assessment';

const SCHEDULING_MODAL_CONTENT_TEXT =
  'The assessment takes about 30 minutes and we will text you 30 minutes before arrival.';

/**
 * Picker for slots within a selected date
 *
 */
const TimeslotPicker: React.FC<{
  selectedDate: LocalDate;
  timeslots: LocalDateTime[];
  zoneId: ZoneId;
  selectTime: (date: LocalDateTime) => void;
}> = ({ selectedDate, timeslots, zoneId, selectTime }) => {
  // Filter to specific day
  // if a specific day is available, there is at least 1 slot for that day
  const filteredTimeslots = timeslots.filter((slot) => slot.toLocalDate().equals(selectedDate));

  filteredTimeslots.sort((a, b) => {
    if (a.isAfter(b)) {
      return 1;
    }
    return -1;
  });

  const morningSlot = filteredTimeslots.find((slot) =>
    slot.isBefore(slot.toLocalDate().atTime(LocalTime.NOON)),
  );
  const afternoonSlot = filteredTimeslots.find(
    (slot) => !slot.isBefore(slot.toLocalDate().atTime(LocalTime.NOON)),
  );

  return (
    <>
      <Box my={5} textAlign="center">
        <H3>Select a time</H3>
        <Text size="s1" fontWeight={'semibold'}>
          {` ${selectedDate.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}`}
        </Text>
        ,
      </Box>
      {morningSlot && (
        <Button
          key={'ssea-morning'}
          aria-label="Home assessment morning time slot"
          analyticsName="cake-exterior-assessment-morning-time-slot"
          variant="primary"
          type="submit"
          onClick={() => {
            selectTime(morningSlot);
          }}
          mb={5}
        >
          {formatInPersonEaMorningAfternoonTimeslot(morningSlot, zoneId)}
        </Button>
      )}
      {afternoonSlot && (
        <Button
          key={'ssea-afternoon'}
          aria-label="Home assessment afternoon time slot"
          analyticsName="cake-exterior-assessment-afternoon-time-slot"
          variant="primary"
          type="submit"
          onClick={() => {
            selectTime(afternoonSlot);
          }}
          mb={5}
        >
          {formatInPersonEaMorningAfternoonTimeslot(afternoonSlot, zoneId)}
        </Button>
      )}
    </>
  );
};

/**
 * Multi-step agent modal for scheduling an exterior assessment
 * Step 1 - Date selection
 * Step 2 - Timeslot selection
 * component-test-check-ignore storybook-check-ignore
 */
export const SchedulingModalWithDateTime: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ setIsOpen, isOpen }) => {
  const { trackEvent } = useObservability();

  enum SchedulingModalSteps {
    Date = 1,
    Time,
  }

  const {
    availableExteriorAssessmentSlots,
    availableExteriorAssessmentDateTimeSlots,
    setSelectedDate: setExteriorAssessmentTime,
  } = useSSEAContext();
  const { zoneId } = useQualificationContext();

  const ref = React.useRef<HTMLDivElement>(null);
  const { shouldSeeAgentExperience } = useOfferContext();

  const [selectedExteriorAssessmentDate, setSelectedExteriorAssessmentDate] = useState<LocalDate>();
  const [schedulingStep, setSchedulingStep] = useState<SchedulingModalSteps>(
    SchedulingModalSteps.Date,
  );

  const selectTime = (date: LocalDateTime) => {
    setExteriorAssessmentTime(date);
    trackEvent('schedule-a-call', 'exterior-assessment');
    setIsOpen(false);
    setSchedulingStep(SchedulingModalSteps.Date);
  };

  const selectDate = (date: LocalDate) => {
    setSelectedExteriorAssessmentDate(date);
    setSchedulingStep(SchedulingModalSteps.Time);
  };

  const onBack = () => {
    setSchedulingStep(SchedulingModalSteps.Date);
  };

  const onClose = () => {
    trackEvent('schedule-a-call', 'exterior-assessment-close');
    setIsOpen(false);
    setSchedulingStep(SchedulingModalSteps.Date);
  };

  const backButton =
    schedulingStep === SchedulingModalSteps.Time ? (
      <HeaderCornerNavButton
        name="back"
        title={'Back'}
        onClick={onBack}
        iconName="chevron-left"
        aria-label="Go back to previous page"
      />
    ) : undefined;

  const headerSection = (
    <Box width="300px" mt={5} mb={8} textAlign="center">
      <DateHeader
        shouldSeeAgentExperience={shouldSeeAgentExperience}
        titleId={EXTERIOR_ASSESSMENT_MODAL_LABEL}
      />
    </Box>
  );

  return (
    <Modal
      analyticsName="cake-legacy-ssea-scheduling-modal-date-time"
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      showClose
      onRequestClose={onClose}
      aria-labelledby={EXTERIOR_ASSESSMENT_MODAL_LABEL}
      headerProps={{ leftComponent: backButton }}
    >
      <Flex flexDirection="column" justifyContent="center" alignItems="center">
        {schedulingStep === SchedulingModalSteps.Date && (
          <Box ref={ref}>
            {headerSection}
            <Divider />
            <DatePickerWrapper
              onClick={(date) => selectDate(date)}
              options={availableExteriorAssessmentSlots}
              title="Select a day"
            />
          </Box>
        )}
        {schedulingStep === SchedulingModalSteps.Time && selectedExteriorAssessmentDate && (
          <Box style={{ height: ref.current?.offsetHeight, width: ref.current?.offsetWidth }}>
            {headerSection}
            <TimeslotPicker
              selectedDate={selectedExteriorAssessmentDate}
              timeslots={availableExteriorAssessmentDateTimeSlots}
              zoneId={zoneId}
              selectTime={selectTime}
            />
          </Box>
        )}
      </Flex>
    </Modal>
  );
};

const DateHeader: React.FC<{ shouldSeeAgentExperience?: boolean; titleId: string }> = ({
  shouldSeeAgentExperience,
  titleId,
}) => {
  const schedulingModelContentText = SCHEDULING_MODAL_CONTENT_TEXT;
  const modalTitle = shouldSeeAgentExperience
    ? SCHEDULING_MODAL_AGENT_TITLE_TEXT
    : SCHEDULING_MODAL_TITLE_TEXT;

  return (
    <>
      <Heading id={titleId} size="h3" mb={3}>
        {modalTitle}
      </Heading>
      <Text fontSize="s0" lineHeight="s00" textAlign="left">
        {schedulingModelContentText}
      </Text>
    </>
  );
};
